/**
 * ErrorBoundary.jsx
 * Componente que atrapa errores de React y muestra un fallback visual
 * en lugar de romper toda la app con pantalla blanca.
 * 
 * Se usa envolviendo <Routes> en App.js para proteger toda la aplicación.
 * 
 * React requiere que los Error Boundaries sean CLASES, no funciones.
 */

import React, { Component } from 'react';
import './ErrorBoundary.css';

// TASK-014: guard anti-loop para la auto-recuperación de ChunkLoadError
// (permite 1 reload automático por carga de página tras un deploy/fallo de red)
const CHUNK_RELOAD_KEY = 'uturno_chunk_reload_attempted';

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        // Estado inicial: sin error
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    // Actualizar estado cuando ocurre un error en un componente hijo
    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    // Capturar información adicional del error (stack trace del componente)
    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });

        // TASK-014: ChunkLoadError = la app quedó con main.js/chunks viejos tras un
        // deploy (o fallo de red al descargar la ruta lazy). Recargar UNA vez para
        // tomar la versión nueva; si vuelve a fallar, mostrar el fallback normal.
        if (error?.name === 'ChunkLoadError') {
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    if (!sessionStorage.getItem(CHUNK_RELOAD_KEY)) {
                        sessionStorage.setItem(CHUNK_RELOAD_KEY, '1');
                        window.location.reload();
                        return;
                    }
                    sessionStorage.removeItem(CHUNK_RELOAD_KEY);
                }
            } catch (e) {
                // Silencioso en caso de restricciones de almacenamiento del navegador o crawler
            }
        }

        // Loguear el error en consola (futuro: enviar a servicio como Sentry)
        console.error('[ErrorBoundary] Se atrapó un error:', error);
        console.error('[ErrorBoundary] Componente stack:', errorInfo?.componentStack);
    }

    // Reiniciar el estado para intentar renderizar de nuevo
    handleReload = () => {
        this.setState({ hasError: false, error: null, errorInfo: null });
        window.location.reload();
    };

    render() {
        // Si hay error, mostrar pantalla de fallback en vez de pantalla blanca
        if (this.state.hasError) {
            return (
                <div className="eb-container">
                    <div className="eb-icon">⚠️</div>
                    <h1 className="eb-title">Algo salió mal</h1>
                    <p className="eb-message">
                        Ocurrió un error inesperado en la aplicación. Intentá recargar la página.
                        Si el problema persiste, contactanos por WhatsApp.
                    </p>
                    <button className="eb-reload" onClick={this.handleReload}>
                        Recargar Página
                    </button>

                    {/* Detalles técnicos colapsables (para desarrollo/debugging) */}
                    {process.env.NODE_ENV === 'development' && this.state.error && (
                        <details className="eb-details">
                            <summary>Ver detalles técnicos</summary>
                            <pre>
                                {this.state.error.toString()}
                                {this.state.errorInfo?.componentStack}
                            </pre>
                        </details>
                    )}
                </div>
            );
        }

        // Si no hay error, renderizar los hijos normalmente
        return this.props.children;
    }
}

export default ErrorBoundary;
